import type { ReactNode } from 'react';
import { Field } from '../../components/ui';
import { formatNumber } from '../../lib/format';
import { REC_COST_TYPES, REC_PRIORITIES, blankCostLine, bucketTotals, costLinesOf } from '../../lib/amCosts';
import { STATUS_BADGES, STATUS_LABELS, controlStatus, type ControlStatus } from '../../lib/amRag';
import type { AmCostLine, AmRecommendation } from '../../lib/types';

/**
 * The pieces every account-management view shares.
 *
 * Risk, priority and status all use the same badge ladder — neutral navy is
 * fine, crimson climbs — so a "High" means the same shade wherever it appears.
 * The source app used a red/amber/green traffic light; this palette has no green
 * or amber, so the severity is carried by the label plus the crimson ramp.
 */

const RISK_BADGES: Record<string, string> = {
  Critical: 'badge-critical',
  High: 'badge-serious',
  Medium: 'badge-warning',
  Low: 'badge-good',
};

export function riskBadgeClass(risk: string | null | undefined): string {
  return RISK_BADGES[risk ?? ''] ?? 'badge-neutral';
}

export function RiskBadge({ risk }: { risk: string | null | undefined }) {
  if (!risk) return <span className="subtle">—</span>;
  return <span className={`badge ${riskBadgeClass(risk)}`}>{risk}</span>;
}

/** Priority sits one notch below risk deliberately: a Critical risk is a fact
 *  about the environment, a Critical priority is a decision about the queue. */
const PRIORITY_BADGES: Record<string, string> = {
  Critical: 'badge-critical',
  High: 'badge-serious',
  Medium: 'badge-warning',
  Low: 'badge-neutral',
};

export function PriorityBadge({ priority }: { priority: string | null | undefined }) {
  if (!priority) return <span className="subtle">—</span>;
  return (
    <span className={`badge ${PRIORITY_BADGES[priority] ?? 'badge-neutral'}`}>{priority}</span>
  );
}

const STATE_BADGES: Record<string, string> = {
  Client: 'badge-good',
  Prospect: 'badge-neutral',
  'At Risk': 'badge-warning',
  Churned: 'badge-critical',
  Inactive: 'badge-neutral',
  Draft: 'badge-neutral',
  'In Progress': 'badge-neutral',
  Completed: 'badge-good',
  Proposed: 'badge-neutral',
  Approved: 'badge-good',
  Declined: 'badge-critical',
  /* A scope of work. 'Approved' above is shared with a recommendation, which
     is the same answer to the same question and should not look different
     depending on which table it came from. */
  New: 'badge-neutral',
  'Needs approval': 'badge-warning',
  Closed: 'badge-neutral',
};

export function StateBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  return <span className={`badge ${STATE_BADGES[status] ?? 'badge-neutral'}`}>{status}</span>;
}

export function ControlBadge({ response }: { response: string | null | undefined }) {
  const status = controlStatus(response);
  return (
    <span className={`badge ${STATUS_BADGES[status]}`}>{response?.trim() || STATUS_LABELS[status]}</span>
  );
}

/** A recommendation's cost, one stacked line per entry. A single figure would
 *  hide the difference between $2,000 once and $2,000 a month. */
export function CostCell({ rec }: { rec: Partial<AmRecommendation> }) {
  const lines = costLinesOf(rec);
  return (
    <>
      {lines.map((line, i) => (
        <div key={i} className={i > 0 ? 'mt-2' : undefined}>
          {formatNumber(line.cost, 'currency')}
          <div className="xs muted">{line.costType}</div>
        </div>
      ))}
    </>
  );
}

/** 0-100 health, as a bar. Below 60 is the number worth noticing. */
export function HealthMeter({ score }: { score: number | null }) {
  const value = Math.max(0, Math.min(100, score ?? 0));
  return (
    <div>
      <div className="row-between">
        <span className="xs muted">Health score</span>
        <strong>{score ?? '—'}</strong>
      </div>
      <div className="meter mt-2" aria-hidden="true">
        <span className={value < 60 ? 'low' : ''} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

/**
 * The audit score, as a ring.
 *
 * A percentage in a box does not land in a client meeting; the ring does, and
 * it is the one figure the whole review hangs off. Crimson below 50, navy above
 * — there is no green in this palette to reward a good score with, so a good
 * score simply stops being crimson.
 */
export function ScoreDial({ score, label = 'Secure' }: { score: number; label?: string }) {
  const colour =
    score >= 80
      ? 'var(--brand-700)'
      : score >= 50
        ? 'var(--brand-400)'
        : 'var(--accent-600)';
  return (
    <div
      className="am-dial"
      style={{ background: `conic-gradient(${colour} ${score * 3.6}deg, var(--gray-200) 0deg)` }}
      role="img"
      aria-label={`${score}% ${label}`}
    >
      <div className="am-dial-inner">
        <div className="am-dial-num" style={{ color: colour }}>
          {score}%
        </div>
        <div className="am-dial-label">{label}</div>
      </div>
    </div>
  );
}

/** In place / partial / gap, as a proportional bar plus counts. */
export function ControlSplit({
  counts,
}: {
  counts: Record<ControlStatus, number>;
}) {
  const shown: ControlStatus[] = ['in-place', 'partial', 'gap'];
  const total = shown.reduce((sum, key) => sum + counts[key], 0);
  if (total === 0) return null;
  return (
    <>
      <div
        className="am-split"
        title={shown.map((k) => `${counts[k]} ${STATUS_LABELS[k].toLowerCase()}`).join(' · ')}
      >
        {shown.map((key) =>
          counts[key] > 0 ? (
            <span key={key} className={`am-split-seg ${key}`} style={{ flex: counts[key] }} />
          ) : null,
        )}
      </div>
      <div className="row wrap mt-2" style={{ gap: 'var(--space-2)' }}>
        {shown.map((key) => (
          <span key={key} className="xs muted">
            <span className={`am-dot ${key}`} /> {counts[key]}{' '}
            {/* "1 gap" but "3 gaps" — the other two labels are already phrases
                that read the same either way. */}
            {key === 'gap' && counts[key] !== 1 ? 'gaps' : STATUS_LABELS[key].toLowerCase()}
          </span>
        ))}
      </div>
    </>
  );
}

/** Per-domain answer dots — the "where is it weak" view a table cannot give. */
export function ControlDomains({
  items,
}: {
  items: { AmQuestionId: string; Category: string | null; Text: string; Response: string | null }[];
}) {
  const domains = new Map<string, typeof items>();
  for (const item of items) {
    const key = item.Category ?? 'Other';
    domains.set(key, [...(domains.get(key) ?? []), item]);
  }
  return (
    <div className="am-domains">
      {[...domains.entries()].map(([domain, list]) => (
        <div key={domain} className="am-domain">
          <div className="am-domain-name">{domain}</div>
          <div className="am-domain-dots">
            {list.map((item) => (
              <span
                key={item.AmQuestionId}
                className={`am-dot ${controlStatus(item.Response)}`}
                title={`${item.Text} — ${STATUS_LABELS[controlStatus(item.Response)]}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Repeatable priority / cost / cost-type rows.
 *
 * One row behaves exactly like the old three-field block. Extra rows are the
 * point: a recommendation routinely has a one-time build cost AND a monthly run
 * cost, and pricing it as a single figure means quoting the wrong number.
 */
export function CostLinesEditor({
  lines,
  onChange,
}: {
  lines: AmCostLine[];
  onChange: (next: AmCostLine[]) => void;
}) {
  const rows = lines.length > 0 ? lines : [blankCostLine()];

  const update = (index: number, patch: Partial<AmCostLine>) =>
    onChange(rows.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const totals = bucketTotals([{ CostLines: rows } as Partial<AmRecommendation>]);
  const parts = [
    totals.oneTime > 0 && `${formatNumber(totals.oneTime, 'currency')} one-time`,
    totals.monthly > 0 && `${formatNumber(totals.monthly, 'currency')}/mo`,
    totals.annual > 0 && `${formatNumber(totals.annual, 'currency')}/yr`,
  ].filter((p): p is string => Boolean(p));

  return (
    <Field label="Priority and price" hint="One row per price. A one-time build price and a monthly price are two rows.">
      <div className="am-cost-head">
        <span className="xs muted">Priority</span>
        <span className="xs muted">Price</span>
        <span className="xs muted">Price type</span>
        <span />
      </div>
      {rows.map((line, i) => (
        <div className="am-cost-row" key={i}>
          <select
            className="select"
            value={line.priority}
            onChange={(e) => update(i, { priority: e.target.value as AmCostLine['priority'] })}
            aria-label="Priority"
          >
            {REC_PRIORITIES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={line.cost}
            onChange={(e) => update(i, { cost: Number(e.target.value) || 0 })}
            aria-label="Price"
          />
          <select
            className="select"
            value={line.costType}
            onChange={(e) => update(i, { costType: e.target.value as AmCostLine['costType'] })}
            aria-label="Price type"
          >
            {REC_COST_TYPES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            title="Remove this price line"
            /* Always leave one row: a recommendation with no cost line has
               nothing to price. */
            disabled={rows.length <= 1}
            onClick={() => onChange(rows.filter((_, n) => n !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-ghost btn-sm mt-2"
        onClick={() => onChange([...rows, blankCostLine()])}
      >
        + Add price row
      </button>
      {rows.length > 1 && parts.length > 0 && (
        <p className="xs muted mt-2">Total: {parts.join(' + ')}</p>
      )}
    </Field>
  );
}

/** The three bucket tiles used by the pipeline and the recommendations tab. */
export function BucketTiles({
  recs,
  hours,
}: {
  recs: Partial<AmRecommendation>[];
  hours?: number;
}) {
  const totals = bucketTotals(recs);
  return (
    <div className={`grid ${hours === undefined ? 'grid-3' : 'grid-4'} mb-4`}>
      <div className="stat">
        <div className="label">One-time</div>
        <div className="figure">{formatNumber(totals.oneTime, 'currency')}</div>
        <div className="foot">project and build price</div>
      </div>
      <div className="stat">
        <div className="label">Monthly recurring</div>
        <div className="figure">{formatNumber(totals.monthly, 'currency')}</div>
        <div className="foot">{formatNumber(totals.monthly * 12, 'currency')} a year</div>
      </div>
      <div className="stat">
        <div className="label">Annual recurring</div>
        <div className="figure">{formatNumber(totals.annual, 'currency')}</div>
        <div className="foot">billed yearly</div>
      </div>
      {hours !== undefined && (
        <div className="stat">
          <div className="label">Labour committed</div>
          <div className="figure">{formatNumber(hours, 'hours')}</div>
          <div className="foot">estimated across these items</div>
        </div>
      )}
    </div>
  );
}

/**
 * The app's primary tab bar.
 *
 * A segmented track rather than the underlined row it used to be: the active
 * tab is a raised pill on a sunken rail, which reads as a control you operate
 * instead of a heading that happens to be underlined. Eight of them across a
 * client is enough that "which one am I on" has to be answerable at a glance.
 *
 * NOT the `.tabs` class. That one is still used by a dozen ad-hoc rows around
 * the app — the framework picker, the CSAT date range, the audit matrix tiers —
 * whose buttons do not carry a `.tab` class at all, so restyling the container
 * they sit in would have moved a lot of things nobody asked to move. Those are
 * secondary switches inside a page; this is the page's own navigation, and the
 * two looking different is the honest outcome.
 *
 * THE ICON IS OPTIONAL and the bar is designed to look finished without one, so
 * the tab sets that have not been given icons yet are not visibly half-done.
 * Where they exist they come from the same glyph vocabulary as the sidebar, so
 * they render in the same font at the same weight rather than importing an
 * icon set for eleven characters.
 */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  prominent = false,
}: {
  tabs: { key: T; label: string; count?: number; icon?: string }[];
  active: T;
  onChange: (key: T) => void;
  /** The page's main navigation rather than a row inside it: full width,
   *  larger, with the active tab filled. */
  prominent?: boolean;
}) {
  return (
    <div className={`tabbar${prominent ? ' tabbar-prominent' : ''}`} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          className={`tabbar-tab${active === tab.key ? ' active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {/* Decorative: the label beside it already says what this is, and a
              screen reader announcing "black circle Compliance" is worse than
              one that does not. */}
          {tab.icon && (
            <span className="tabbar-icon" aria-hidden="true">
              {tab.icon}
            </span>
          )}
          {tab.label}
          {tab.count !== undefined && tab.count > 0 && (
            <span className="tabbar-count">{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/** The signature block every client-facing document ends with. */
export function SignatureBlock({ companyName }: { companyName: string }) {
  return (
    <>
      <div className="am-doc-title">Authorised representative</div>
      <p className="xs muted no-print">
        Must be signed by someone with decision-making authority — CEO, President, Owner, CFO, COO,
        or an IT Director where authorised.
      </p>
      <div className="am-sign-grid">
        <div className="am-sign">
          <span>Name</span>
          <div className="line" />
        </div>
        <div className="am-sign">
          <span>Title</span>
          <div className="line" />
        </div>
        <div className="am-sign">
          <span>Company</span>
          <div className="line filled">{companyName}</div>
        </div>
        <div className="am-sign">
          <span>Date</span>
          <div className="line" />
        </div>
        <div className="am-sign wide">
          <span>Signature</span>
          <div className="line" />
        </div>
      </div>
    </>
  );
}

/**
 * The "pick items, then send" panel shared by the proposal, the risk-acceptance
 * form and the review follow-up.
 *
 * A mailto: link rather than sending from the server. The email goes out from
 * the account manager's own mailbox, in their own thread, and this app has no
 * business impersonating them — nor any mail credentials to do it with.
 */
export function SendPanel({
  recipient,
  onRecipient,
  suggestions,
  subject,
  body,
  disabled,
  copyLabel = 'Copy document text',
  copyText,
  children,
}: {
  recipient: string;
  onRecipient: (value: string) => void;
  suggestions: { value: string; label: string }[];
  subject: string;
  body: string;
  disabled: boolean;
  copyLabel?: string;
  copyText: () => string;
  children?: ReactNode;
}) {
  const href = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
  const listId = `am-recipients-${subject.replace(/\W+/g, '')}`;

  return (
    <section className="card mb-4 no-print">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Send it</span>
          <h2>To the client</h2>
        </div>
      </div>
      <div className="card-body">
        <div className="row wrap">
          <input
            className="input"
            style={{ maxWidth: 320 }}
            placeholder="name@client.example"
            value={recipient}
            onChange={(e) => onRecipient(e.target.value)}
            list={listId}
            aria-label="Recipient email"
          />
          <datalist id={listId}>
            {suggestions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </datalist>
          <a
            className="btn btn-primary"
            href={!disabled && recipient ? href : undefined}
            aria-disabled={disabled || !recipient}
            onClick={(e) => {
              if (disabled || !recipient) e.preventDefault();
            }}
          >
            Open in email
          </a>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={disabled}
            onClick={() => void navigator.clipboard.writeText(copyText())}
          >
            {copyLabel}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => window.print()}>
            Print / save PDF
          </button>
        </div>
        {children}
        <p className="xs muted mt-2">
          Print to PDF first and attach it — the email body carries the summary and the ask, the PDF
          carries the document that gets signed.
        </p>
      </div>
    </section>
  );
}
